import { Table, TableProps } from 'antd';
import update from 'immutability-helper';
import { JSX, useCallback, useRef } from 'react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import './styles.scss';

const type = 'DraggableBodyRow';

const DraggableBodyRow = ({ index, moveRow, className, style, ...restProps }) => {
  const ref = useRef<HTMLTableRowElement>(null);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: (monitor) => {
      const { index: dragIndex }: any = monitor.getItem() || {};
      if (dragIndex === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragIndex < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: any) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));

  return (
    <tr
      ref={ref}
      className={`${className}${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', ...style }}
      {...restProps}
    />
  );
};

interface DnDTableProps<RecordType> extends TableProps<RecordType> {
  onMoveRow?: (data: readonly RecordType[]) => void;
  onMove?: (fromIndex: number, toIndex: number) => void;
}

const DnDTable = <RecordType extends object = any>(
  props: DnDTableProps<RecordType>
): JSX.Element => {
  const { dataSource, children, onMoveRow, onRow, onMove } = props;

  const components = {
    body: {
      row: DraggableBodyRow,
    },
  };

  const moveRow = useCallback(
    (dragIndex, hoverIndex) => {
      if (onMove) {
        onMove(dragIndex, hoverIndex);
      }

      if (!onMoveRow || !dataSource) return;
      const dragRow = dataSource?.[dragIndex];

      onMoveRow(
        update(dataSource as any, {
          $splice: [
            [dragIndex, 1],
            [hoverIndex, 0, dragRow],
          ],
        })
      );
    },
    [dataSource]
  );

  const _onRow = (record: any, index: any): any => ({
    index,
    moveRow,
    ...onRow,
  });

  return (
    <div className="cbra-dnd-table__container">
      <DndProvider backend={HTML5Backend}>
        <Table {...props} components={components} onRow={_onRow}>
          {children}
        </Table>
      </DndProvider>
    </div>
  );
};

export default DnDTable;
